/**
 * パイプライン cleanup ステップ実装。
 *
 * **ゲート条件: 非ヒーロー ∧ 外装カテゴリ (2-5) ∧ Claude が hasReflection=true と判定**
 *
 * Wave 履歴:
 * - Wave 7-10: hasReflection=true でゲート → Haiku が鏡像漢字を検出できず
 *   検出漏れで機能しない
 * - Wave 11: ゲート廃止、外装全件を Gemini に送る → Gemini Pro が映っていない
 *   写真にもハルシネーションで福山工業所を合成する副作用 (Wave 15a 本番テスト)
 * - Wave 15c: Claude を Sonnet に昇格 + classify プロンプトを厳密化した上で
 *   ゲート復活。Sonnet の鏡像検出精度向上で検出漏れを抑え、かつ映ってない
 *   写真は Gemini に送らないのでハルシネーション物理不可能。
 *
 * 並列度制御 + フォールバック (失敗は元画像のまま) + 進捗通知。
 */

import type { CategoryId, ClassificationResult } from '@shared/types';

import type { ProcessedImage, Result } from '../image';
import type { CleanupBlobFn } from './browser-deps';
import type { PipelineProgress } from './types';

/**
 * 反射除去対象の外装カテゴリ。
 * 1 (hero) は別ステップ (compose) で処理するので除外。
 * 6-14 は内装・装備アップなので店舗ガラスの映り込みが入らない。
 */
const EXTERIOR_CATEGORY_IDS: readonly CategoryId[] = [2, 3, 4, 5] as const;

function isExteriorCategory(id: CategoryId | undefined): boolean {
  return id !== undefined && EXTERIOR_CATEGORY_IDS.includes(id);
}

export interface CleanupStageInput {
  readonly files: readonly File[];
  readonly heroIndex: number;
  readonly classifications: readonly ClassificationResult[];
  readonly processed: ReadonlyMap<number, Result<ProcessedImage>>;
  readonly cleanupFn: CleanupBlobFn;
  readonly concurrency: number;
  readonly report: (p: PipelineProgress) => void;
}

export interface CleanupStageResult {
  readonly map: ReadonlyMap<number, Result<ProcessedImage>>;
  readonly cleanedCount: number;
}

interface CleanupJob {
  readonly inputIdx: number;
  readonly processedImage: ProcessedImage;
}

export async function runCleanupStage(input: CleanupStageInput): Promise<CleanupStageResult> {
  const flagged = collectFlagged(input);
  input.report({ step: 'removing-reflections', current: 0, total: flagged.length });
  if (flagged.length === 0) {
    return { map: input.processed, cleanedCount: 0 };
  }

  const merged = new Map(input.processed);
  const counter = { completed: 0, cleaned: 0 };
  await runConcurrent(input.concurrency, flagged, async (job) => {
    await tryCleanup(job, input, merged, counter, flagged.length);
  });
  return { map: merged, cleanedCount: counter.cleaned };
}

function collectFlagged(input: CleanupStageInput): readonly CleanupJob[] {
  const jobs: CleanupJob[] = [];
  for (let i = 0; i < input.files.length; i++) {
    if (i === input.heroIndex) continue; // hero は compose 側で処理
    const cls = input.classifications[i];
    // Wave 15c: 外装カテゴリ ∧ Claude Sonnet が hasReflection=true
    // 内装・装備アップは物理的に店舗ガラス反射が入らないので除外 (Wave 11 継承)。
    // さらに Sonnet が「実際に鏡像漢字が読み取れる」と判定した写真のみに絞り、
    // 映っていない写真へのハルシネーション誘発を防ぐ (Wave 15a の副作用対策)。
    if (!isExteriorCategory(cls?.categoryId)) continue;
    if (!cls?.hasReflection) continue;
    const res = input.processed.get(i);
    if (!res?.ok) continue;
    jobs.push({ inputIdx: i, processedImage: res.value });
  }
  return jobs;
}

async function tryCleanup(
  job: CleanupJob,
  input: CleanupStageInput,
  merged: Map<number, Result<ProcessedImage>>,
  counter: { completed: number; cleaned: number },
  total: number,
): Promise<void> {
  try {
    const cleaned = await input.cleanupFn(job.processedImage.blob);
    merged.set(job.inputIdx, {
      ok: true,
      value: {
        blob: cleaned.blob,
        width: job.processedImage.width,
        height: job.processedImage.height,
        mimeType: cleaned.mimeType,
        processingTimeMs: job.processedImage.processingTimeMs + cleaned.processingTimeMs,
      },
    });
    counter.cleaned++;
  } catch (err) {
    console.warn('[cleanup] falling back for idx', job.inputIdx, err);
  }
  counter.completed++;
  input.report({
    step: 'removing-reflections',
    current: counter.completed,
    total,
  });
}

async function runConcurrent<T>(
  concurrency: number,
  items: readonly T[],
  worker: (item: T) => Promise<void>,
): Promise<void> {
  const parallel = Math.max(1, Math.min(concurrency, items.length));
  let nextIdx = 0;
  const runOne = async (): Promise<void> => {
    while (true) {
      const idx = nextIdx++;
      if (idx >= items.length) return;
      const item = items[idx];
      if (item === undefined) continue;
      await worker(item);
    }
  };
  await Promise.all(Array.from({ length: parallel }, () => runOne()));
}
